import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import TeamPill from "components/team-pill";
import type { Mention } from "services/types/mentions.type";

import {
  buildMentionTaskHref,
  formatMentionMoment,
  groupTeamMentions
} from "./mentions.util";

const firstName = (name: string) => name.split(" ")[0] || name;

/**
 * "Notified: Karen Ruiz ✓ Seen Oct 5, 2:26 PM · Kathy Morales ◷ Not seen yet"
 * "Sent to [Chicago North PCCs] · Seen by 2 of 6 · Tom Alvarez took it · Open Tom's task"
 *
 * Who the writer told and whether they opened it since, so nobody has to ask "did you see my
 * note?". Used under a chart note ("Notified:") and under a task description ("Mentioned:").
 * Rows reached through a team (TEAMS_FEATURE) are grouped per team: one pill, a seen count and who
 * took it. Internal only: it is never part of the text, so print, PDF, fax and email never carry it.
 */
export default function MentionsSummary<T extends Mention>({
  mentions,
  label = "Notified:",
  getTooltip = (mention) =>
    `Notified ${formatMentionMoment(mention.notified_at)}`,
  getTaskId = () => null,
  testId = "chart-note-mentions-summary"
}: {
  mentions: T[] | undefined;
  label?: string;
  getTooltip?: (mention: T) => string;
  /** The follow-up task of a row, for "Open Tom's task" on a taken team mention. */
  getTaskId?: (mention: T) => number | null | undefined;
  testId?: string;
}) {
  if (!mentions?.length) return null;

  const people = mentions.filter((mention) => !mention.team);
  const teams = groupTeamMentions(mentions, getTaskId);

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      columnGap={2}
      rowGap={0.5}
      data-testid={testId}
      sx={{ mt: 1 }}
    >
      {people.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      )}
      {people.map((mention) => (
        <Stack key={mention.id} direction="row" alignItems="center" gap={0.5}>
          <Tooltip title={getTooltip(mention)}>
            <Typography variant="body2" fontWeight={700}>
              {mention.user.name}
            </Typography>
          </Tooltip>
          {mention.seen_at ? (
            <Typography
              variant="body2"
              color="success.main"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
              Seen {formatMentionMoment(mention.seen_at)}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <ScheduleIcon sx={{ fontSize: 16 }} />
              Not seen yet
            </Typography>
          )}
        </Stack>
      ))}
      {teams.map((group) => (
        <Stack
          key={`team-${group.team.id}`}
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          data-testid={`mentions-summary-team-${group.team.id}`}
        >
          <Typography variant="body2" color="text.secondary">
            Sent to
          </Typography>
          <Tooltip title={getTooltip(group.rows[0])}>
            <span>
              <TeamPill name={group.team.name} color={group.team.color} />
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            · Seen by{" "}
            <Typography component="span" variant="body2" fontWeight={700}>
              {group.seen} of {group.rows.length}
            </Typography>
          </Typography>
          {group.takenBy ? (
            <>
              <Typography
                variant="body2"
                color="success.main"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                {group.takenBy.name} took it
              </Typography>
              {group.taskId && (
                <Link
                  href={buildMentionTaskHref(group.taskId)}
                  underline="hover"
                  variant="body2"
                >
                  Open {firstName(group.takenBy.name)}&apos;s task
                </Link>
              )}
            </>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <ScheduleIcon sx={{ fontSize: 16 }} />
              Not taken yet
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
